// Top-bar Notifications widget. Bell icon with unread badge; opens a
// popover listing pending friend requests and game invitations with
// inline actions.

import { css, html } from 'lit';
import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { router } from '../../lib/router.js';
import { notificationsState } from '../../state/notifications-state.js';
import { popoverState } from '../../state/popover-state.js';
import { presenceState } from '../../state/presence-state.js';
import '../shared/app-viewport.js';
import '../views/notifications-view.js';

const POPOVER_ID = 'notifications';

class NotificationsWidget extends BaseComponent {
  static properties = {
    _open: { type: Boolean, state: true },
    _busyId: { type: String, state: true },
    _modalOpen: { type: Boolean, state: true },
  };

  static styles = css`
    :host {
      position: relative;
      display: inline-flex;
    }

    .trigger {
      position: relative;
      background: transparent;
      border: none;
      color: rgba(255, 255, 255, 0.7);
      padding: 4px 6px;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      font-size: 1.4rem;
      line-height: 1;
      transition: color 0.15s;
    }

    .trigger:hover,
    .trigger:focus {
      color: rgba(255, 255, 255, 1);
      outline: none;
    }

    .menu-panel {
      position: absolute;
      top: 100%;
      right: 0;
      margin-top: 4px;
      width: 340px;
      max-height: 480px;
      overflow-y: auto;
      background: #1a1a2a;
      border: 1px solid rgba(255, 255, 255, 0.15);
      border-radius: 6px;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
      z-index: 1500;
      color: rgba(255, 255, 255, 0.9);
    }

    .group-header {
      padding: 8px 12px;
      font-size: 0.75rem;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: rgba(255, 255, 255, 0.5);
      border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    }

    .row {
      padding: 10px 12px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.05);
      font-size: 0.85rem;
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    .row.unread {
      background: rgba(13, 110, 253, 0.06);
    }

    .row-text {
      line-height: 1.35;
    }

    .row-actions {
      display: flex;
      gap: 6px;
    }

    .btn {
      font-size: 0.75rem;
      padding: 3px 8px;
      border-radius: 4px;
      border: 1px solid rgba(255, 255, 255, 0.2);
      background: transparent;
      color: rgba(255, 255, 255, 0.85);
      cursor: pointer;
    }

    .btn.primary {
      border-color: #0d6efd;
      background: #0d6efd;
      color: white;
    }

    .btn:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    .footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 8px 12px;
      border-top: 1px solid rgba(255, 255, 255, 0.08);
    }

    .footer button {
      background: transparent;
      border: none;
      color: #6db4ff;
      cursor: pointer;
      font-size: 0.85rem;
      padding: 0;
    }

    .footer button:hover {
      text-decoration: underline;
    }

    .empty {
      padding: 16px 12px;
      text-align: center;
      font-size: 0.85rem;
      color: rgba(255, 255, 255, 0.55);
    }
  `;

  constructor() {
    super();
    this._open = false;
    this._busyId = '';
    this._modalOpen = false;
    /** @type {(() => void) | null} */
    this._unsubscribe = null;
    /** @type {(() => void) | null} */
    this._unsubscribePopover = null;
    this._handleDocumentClick = this._handleDocumentClick.bind(this);
  }

  async connectedCallback() {
    await super.connectedCallback();
    this._unsubscribe = notificationsState.subscribe(() => this.requestUpdate());
    this._unsubscribePopover = popoverState.subscribe((openId) => {
      this._open = openId === POPOVER_ID;
    });
    notificationsState.refetch();
    document.addEventListener('click', this._handleDocumentClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._unsubscribe?.();
    this._unsubscribe = null;
    this._unsubscribePopover?.();
    this._unsubscribePopover = null;
    document.removeEventListener('click', this._handleDocumentClick);
  }

  /** @param {MouseEvent} e */
  _handleDocumentClick(e) {
    if (!this._open) return;
    const path = e.composedPath();
    if (!path.includes(this)) popoverState.close(POPOVER_ID);
  }

  _toggle(e) {
    e.stopPropagation();
    popoverState.toggle(POPOVER_ID);
  }

  /** @param {string} requestId */
  async _accept(requestId) {
    this._busyId = requestId;
    try {
      await api.acceptFriendRequest(requestId);
      await notificationsState.refetch();
      // Refresh the friends list / presence so the new friend appears
      // in the friends widget without a page reload.
      presenceState.refetch();
    } catch (err) {
      console.warn('[notifications-widget] accept failed', err);
    } finally {
      this._busyId = '';
    }
  }

  /** @param {string} requestId */
  async _reject(requestId) {
    this._busyId = requestId;
    try {
      await api.rejectFriendRequest(requestId);
      await notificationsState.refetch();
    } catch (err) {
      console.warn('[notifications-widget] reject failed', err);
    } finally {
      this._busyId = '';
    }
  }

  /** @param {string} gameId */
  _join(gameId) {
    popoverState.close(POPOVER_ID);
    router.navigate(`/game/${gameId}/join`);
  }

  async _markAllRead(e) {
    e.stopPropagation();
    await notificationsState.markAllRead();
  }

  _viewAll() {
    popoverState.close(POPOVER_ID);
    // Players in fullscreen views (e.g. /game/:id, /game/:id/join) should
    // stay where they are — opening /notifications swaps the underlying
    // layout to 'title' and effectively kicks them out of the game. For
    // those routes, render the notifications view in a local modal
    // instead. Other contexts (title screen, staff) navigate normally.
    if (router.meta?.layout === 'fullscreen') {
      this._modalOpen = true;
    } else {
      router.navigate('/notifications');
    }
  }

  _closeModal() {
    this._modalOpen = false;
  }

  render() {
    const unread = notificationsState.unreadCount;
    const notifications = notificationsState.notifications;
    const friendRequests = notifications.filter(
      (n) =>
        n.kind === 'friend_request' &&
        n.friendRequest?.direction === 'incoming' &&
        n.friendRequest?.status === 'pending',
    );
    const gameInvitations = notifications.filter(
      (n) => n.kind === 'game_invitation' && n.gameInvitation?.status === 'pending',
    );

    return html`
      <button class="trigger" @click="${this._toggle}" title="Notifications" aria-label="Notifications">
        <i class="bi bi-bell-fill"></i>
        ${
          unread > 0
            ? html`<span class="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger" style="font-size: 0.6rem;">${unread}</span>`
            : ''
        }
      </button>
      ${
        this._open
          ? html`
            <div class="menu-panel" @click="${(/** @type {Event} */ e) => e.stopPropagation()}">
              ${
                friendRequests.length === 0 && gameInvitations.length === 0
                  ? html`<div class="empty">No notifications.</div>`
                  : html`
                    ${
                      friendRequests.length > 0
                        ? html`
                          <div class="group-header">Friend requests</div>
                          ${friendRequests.map(
                            (n) => html`
                              <div class="row ${n.read ? '' : 'unread'}">
                                <div class="row-text">
                                  <strong>${n.friendRequest.otherUsername}</strong> wants to be your friend.
                                </div>
                                <div class="row-actions">
                                  <button
                                    class="btn primary"
                                    ?disabled="${this._busyId === n.friendRequest.id}"
                                    @click="${() => this._accept(n.friendRequest.id)}"
                                  >
                                    Accept
                                  </button>
                                  <button
                                    class="btn"
                                    ?disabled="${this._busyId === n.friendRequest.id}"
                                    @click="${() => this._reject(n.friendRequest.id)}"
                                  >
                                    Decline
                                  </button>
                                </div>
                              </div>
                            `,
                          )}
                        `
                        : ''
                    }
                    ${
                      gameInvitations.length > 0
                        ? html`
                          <div class="group-header">Game invitations</div>
                          ${gameInvitations.map(
                            (n) => html`
                              <div class="row ${n.read ? '' : 'unread'}">
                                <div class="row-text">
                                  <strong>${n.gameInvitation.inviterUsername}</strong> invited you to
                                  <em>${n.gameInvitation.gameAdventureName}</em>.
                                </div>
                                <div class="row-actions">
                                  <button class="btn primary" @click="${() => this._join(n.gameInvitation.gameId)}">Join</button>
                                </div>
                              </div>
                            `,
                          )}
                        `
                        : ''
                    }
                  `
              }
              <div class="footer">
                <button @click="${this._markAllRead}" ?disabled="${unread === 0}">Mark all read</button>
                <button @click="${this._viewAll}">View all →</button>
              </div>
            </div>
          `
          : ''
      }
      ${
        this._modalOpen
          ? html`
            <app-viewport heading="Notifications" @close="${this._closeModal}">
              <notifications-view></notifications-view>
            </app-viewport>
          `
          : ''
      }
    `;
  }
}

customElements.define('notifications-widget', NotificationsWidget);
